import { BANNED_IOR_ADDRESSES, BannedAddressEntry } from '@constants/banned-addresses.const';

/**
 * Loose AddressModel / company address form-group value shape — only the fields
 * the ban check reads. `state`/`country` may be null while the form is being filled.
 */
export interface BannedAddressCheckValue {
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  city?: string | null;
  postalCode?: string | null;
  state?: { code?: string | null } | null;
  country?: { code?: string | null } | null;
}

// Canonical forms for common address abbreviations so 'STREET'/'STR' ≡ 'ST',
// 'NORTH' ≡ 'N', 'SUITE' ≡ 'STE', etc. — makes the token match resilient to spelling variants.
const CANONICAL_ADDRESS_TOKENS: { [token: string]: string } = {
  STREET: 'ST',
  STR: 'ST',
  AVENUE: 'AVE',
  AV: 'AVE',
  ROAD: 'RD',
  DRIVE: 'DR',
  DRV: 'DR',
  BOULEVARD: 'BLVD',
  LANE: 'LN',
  SUITE: 'STE',
  UNIT: 'STE',
  APARTMENT: 'APT',
  NORTH: 'N',
  SOUTH: 'S',
  EAST: 'E',
  WEST: 'W',
};

/** Uppercase, replace every non-alphanumeric run with a space, split, canonicalize. */
export function tokenizeAddressText(value: string | null | undefined): string[] {
  return (value ?? '')
    .toString()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .split(' ')
    .filter((token: string) => token !== '')
    .map((token: string) => CANONICAL_ADDRESS_TOKENS[token] ?? token);
}

function normalizeCompact(value: string | null | undefined): string {
  return tokenizeAddressText(value).join(' ');
}

/**
 * #13189 — whole-building match against the banned registered-agent address list:
 * country + state + ZIP (5-digit base, ZIP+4 tolerated) + city must match exactly
 * (normalized), and every banned street token must be present within a SINGLE
 * address line's tokens (order-independent within the line, suite ignored).
 * Per-line matching avoids false positives from unrelated lines contributing
 * tokens (e.g. line1 "30 Main St" + line2 "N Gould Ave" must NOT match).
 */
export function isBannedIorAddress(address: BannedAddressCheckValue | null | undefined): boolean {
  if (!address) {
    return false;
  }

  const country: { code?: string | null } = address.country ?? {};
  const state: { code?: string | null } = address.state ?? {};
  const countryCode: string = (country.code ?? '').toUpperCase();
  const stateCode: string = (state.code ?? '').toUpperCase();
  const zip: string = (address.postalCode ?? '')
    .toString()
    .replace(/[^a-z0-9]/gi, '')
    .toUpperCase();
  const city: string = normalizeCompact(address.city);
  const lineTokenSets: string[][] = [
    tokenizeAddressText(address.line1),
    tokenizeAddressText(address.line2),
    tokenizeAddressText(address.line3),
  ];

  return BANNED_IOR_ADDRESSES.some(
    (entry: BannedAddressEntry) =>
      countryCode === entry.countryCode &&
      stateCode === entry.stateCode &&
      (zip === entry.postalCode || (zip.length > entry.postalCode.length && zip.startsWith(entry.postalCode))) &&
      city === entry.city &&
      lineTokenSets.some((lineTokens: string[]) => entry.streetTokens.every((token: string) => lineTokens.includes(token))),
  );
}
